import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Card } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { 
  ArrowLeft, Users, Plus, X, Star, Clock, DollarSign, Car, Phone, Mail, 
  Shield, RefreshCw, Search, CheckCircle, AlertTriangle, Coffee, Send,
  FileText, Trash2, Calendar, TrendingUp, MessageSquare, Upload, Loader2
} from 'lucide-react';
import { toast } from '../hooks/use-toast';

const API = process.env.REACT_APP_BACKEND_URL;

const DAYS = ['monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday', 'sunday'];
const DAY_LABELS = { monday: 'Mon', tuesday: 'Tue', wednesday: 'Wed', thursday: 'Thu', friday: 'Fri', saturday: 'Sat', sunday: 'Sun' };
const AVAIL_COLORS = { available: 'bg-green-100 text-green-800 border-green-300', unavailable: 'bg-red-100 text-red-800 border-red-300', on_break: 'bg-yellow-100 text-yellow-800 border-yellow-300' };
const AVAIL_ICONS = { available: CheckCircle, unavailable: AlertTriangle, on_break: Coffee };

const AdminDrivers = () => {
  const navigate = useNavigate();
  const [tab, setTab] = useState('roster');
  const [drivers, setDrivers] = useState([]);
  const [overview, setOverview] = useState(null);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [showForm, setShowForm] = useState(false);
  const [selectedDriver, setSelectedDriver] = useState(null);
  const [detailTab, setDetailTab] = useState('info');
  const [messages, setMessages] = useState([]);
  const [msgText, setMsgText] = useState('');
  const [documents, setDocuments] = useState([]);
  const [payroll, setPayroll] = useState([]);
  const [performance, setPerformance] = useState(null);
  const [newDriver, setNewDriver] = useState({
    name: '', email: '', phone: '', license_number: '', vehicle_assigned: '', hourly_rate: 30, notes: ''
  });
  const [docUpload, setDocUpload] = useState({ type: 'drivers_license', file: null, expires_at: '' });
  const [docUploading, setDocUploading] = useState(false);

  const loadDrivers = useCallback(async () => {
    try {
      const [driversRes, overviewRes] = await Promise.all([
        fetch(`${API}/api/drivers`),
        fetch(`${API}/api/drivers/dashboard/overview`)
      ]);
      setDrivers((await driversRes.json()).drivers || []);
      setOverview(await overviewRes.json());
    } catch (err) { console.error(err); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { loadDrivers(); }, [loadDrivers]);

  const loadDriverDetails = async (driverId) => {
    try {
      const [msgRes, docRes, payRes, perfRes] = await Promise.all([
        fetch(`${API}/api/drivers/${driverId}/messages`),
        fetch(`${API}/api/drivers/${driverId}/documents`),
        fetch(`${API}/api/drivers/${driverId}/payroll`),
        fetch(`${API}/api/drivers/${driverId}/performance`),
      ]);
      setMessages((await msgRes.json()).messages || []);
      setDocuments((await docRes.json()).documents || []);
      setPayroll((await payRes.json()).payroll || []);
      setPerformance(await perfRes.json());
    } catch (err) { console.error(err); }
  };

  const selectDriver = (driver) => {
    setSelectedDriver(driver);
    setDetailTab('info');
    loadDriverDetails(driver.driver_id);
  };

  const handleCreate = async (e) => {
    e.preventDefault();
    try {
      const res = await fetch(`${API}/api/drivers`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(newDriver)
      });
      if (res.ok) {
        toast({ title: 'Driver Added' });
        setShowForm(false);
        setNewDriver({ name: '', email: '', phone: '', license_number: '', vehicle_assigned: '', hourly_rate: 30, notes: '' });
        loadDrivers();
      }
    } catch { toast({ title: 'Error', variant: 'destructive' }); }
  };

  const handleDocUpload = async () => {
    if (!docUpload.file || !selectedDriver) return;
    setDocUploading(true);
    try {
      const formData = new FormData();
      formData.append('file', docUpload.file);
      formData.append('doc_type', docUpload.type);
      formData.append('doc_name', docUpload.file.name);
      formData.append('expires_at', docUpload.expires_at || '');
      const res = await fetch(`${API}/api/drivers/${selectedDriver.driver_id}/documents/upload`, {
        method: 'POST', body: formData
      });
      const data = await res.json();
      if (data.document) {
        toast({ title: 'Document uploaded successfully' });
        setDocUpload({ type: 'drivers_license', file: null, expires_at: '' });
        loadDriverDetails(selectedDriver.driver_id);
      } else {
        toast({ title: data.detail || 'Upload failed', variant: 'destructive' });
      }
    } catch { toast({ title: 'Upload error', variant: 'destructive' }); }
    setDocUploading(false);
  };

  const handleAvailability = async (driverId, status) => {
    try {
      await fetch(`${API}/api/drivers/${driverId}/availability`, {
        method: 'PUT', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ availability_status: status })
      });
      toast({ title: `Set to ${status.replace('_', ' ')}` });
      loadDrivers();
      if (selectedDriver?.driver_id === driverId) {
        setSelectedDriver({ ...selectedDriver, availability_status: status });
      }
    } catch { toast({ title: 'Error', variant: 'destructive' }); }
  };

  const handleUpdateStatus = async (driverId, status) => {
    try {
      await fetch(`${API}/api/drivers/${driverId}`, {
        method: 'PUT', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ status })
      });
      toast({ title: `Driver ${status}` });
      loadDrivers();
    } catch { toast({ title: 'Error', variant: 'destructive' }); }
  };

  const handleScheduleUpdate = async (driverId, day, slots) => {
    const driver = drivers.find(d => d.driver_id === driverId);
    if (!driver) return;
    const schedule = { ...driver.weekly_schedule, [day]: slots };
    try {
      await fetch(`${API}/api/drivers/${driverId}/schedule`, {
        method: 'PUT', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ weekly_schedule: schedule })
      });
      toast({ title: 'Schedule updated' });
      loadDrivers();
    } catch { toast({ title: 'Error', variant: 'destructive' }); }
  };

  const handleSendMessage = async () => {
    if (!msgText.trim() || !selectedDriver) return;
    try {
      await fetch(`${API}/api/drivers/${selectedDriver.driver_id}/messages`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ text: msgText, from: 'admin' })
      });
      setMsgText('');
      const res = await fetch(`${API}/api/drivers/${selectedDriver.driver_id}/messages`);
      setMessages((await res.json()).messages || []);
    } catch { toast({ title: 'Error', variant: 'destructive' }); }
  };

  const handleDeleteDriver = async (driverId) => {
    if (!window.confirm('Delete this driver?')) return;
    try {
      await fetch(`${API}/api/drivers/${driverId}`, { method: 'DELETE' });
      toast({ title: 'Driver deleted' });
      if (selectedDriver?.driver_id === driverId) setSelectedDriver(null);
      loadDrivers();
    } catch { toast({ title: 'Error', variant: 'destructive' }); }
  };

  const filtered = drivers.filter(d =>
    !search || d.name?.toLowerCase().includes(search.toLowerCase()) || d.email?.toLowerCase().includes(search.toLowerCase())
  );

  if (loading) return <div className="min-h-screen bg-gray-100 flex items-center justify-center"><div className="animate-spin rounded-full h-8 w-8 border-b-2 border-red-600" /></div>;

  return (
    <div className="min-h-screen bg-gray-100" data-testid="admin-drivers">
      <header className="bg-black text-white py-4 sticky top-0 z-40">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 flex items-center justify-between">
          <div className="flex items-center gap-2 sm:gap-4">
            <Button variant="ghost" size="sm" onClick={() => navigate('/admin')} className="text-gray-300 hover:text-white">
              <ArrowLeft className="h-4 w-4 sm:mr-2" /><span className="hidden sm:inline">Back</span>
            </Button>
            <div>
              <h1 className="text-lg sm:text-xl font-bold">Driver Management</h1>
              <p className="text-xs text-gray-400 hidden sm:block">Roster, scheduling, performance</p>
            </div>
          </div>
          <div className="flex items-center gap-2">
            {overview && (
              <div className="flex items-center gap-1 bg-green-600/20 px-3 py-1 rounded-full">
                <CheckCircle className="h-3 w-3 text-green-400" />
                <span className="text-xs text-green-400 font-medium">{overview.available_now} Available</span>
              </div>
            )}
            <Button variant="outline" size="sm" onClick={loadDrivers} className="border-gray-700 text-gray-300 hover:text-white">
              <RefreshCw className="h-4 w-4" />
            </Button>
          </div>
        </div>
      </header>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 py-6">
        {/* Overview Stats */}
        {overview && (
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-3 mb-6">
            {[
              { label: 'Total Drivers', value: overview.total_drivers, icon: Users, bg: 'bg-blue-100', color: 'text-blue-600' },
              { label: 'Available', value: overview.available_now, icon: CheckCircle, bg: 'bg-green-100', color: 'text-green-600' },
              { label: 'On Break', value: overview.on_break, icon: Coffee, bg: 'bg-yellow-100', color: 'text-yellow-600' },
              { label: 'Pending Apps', value: overview.pending_applications, icon: AlertTriangle, bg: 'bg-orange-100', color: 'text-orange-600' },
              { label: 'Total Rides', value: overview.fleet_stats?.total_rides || 0, icon: Car, bg: 'bg-purple-100', color: 'text-purple-600' },
              { label: 'Avg Rating', value: overview.fleet_stats?.avg_rating || '-', icon: Star, bg: 'bg-yellow-100', color: 'text-yellow-600' },
            ].map(({ label, value, icon: Icon, bg, color }, i) => (
              <Card key={i} className="p-3 border-gray-200" data-testid={`driver-stat-${i}`}>
                <div className="flex items-center gap-2">
                  <div className={`h-8 w-8 rounded-lg ${bg} flex items-center justify-center flex-shrink-0`}>
                    <Icon className={`h-4 w-4 ${color}`} />
                  </div>
                  <div><p className="text-lg font-bold text-black">{value}</p><p className="text-[10px] text-gray-500">{label}</p></div>
                </div>
              </Card>
            ))}
          </div>
        )}

        <div className="flex flex-col lg:flex-row gap-6">
          {/* Driver List */}
          <div className={`${selectedDriver ? 'hidden lg:block lg:w-1/3' : 'w-full'} space-y-4`}>
            <div className="flex items-center gap-3">
              <div className="relative flex-1">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
                <Input placeholder="Search drivers..." value={search} onChange={e => setSearch(e.target.value)} className="pl-10 h-11 sm:h-9" data-testid="driver-search" />
              </div>
              <Button onClick={() => setShowForm(true)} className="bg-red-600 hover:bg-red-700 flex-shrink-0 h-11 sm:h-9" data-testid="add-driver-btn">
                <Plus className="h-4 w-4 sm:mr-2" /><span className="hidden sm:inline">Add Driver</span>
              </Button>
            </div>

            <div className="space-y-2 max-h-[calc(100vh-320px)] overflow-y-auto">
              {filtered.map(driver => {
                const AvailIcon = AVAIL_ICONS[driver.availability_status] || CheckCircle;
                return (
                  <Card key={driver.driver_id}
                    className={`p-3 border-gray-200 cursor-pointer hover:shadow-md transition-shadow active:bg-gray-50 ${selectedDriver?.driver_id === driver.driver_id ? 'ring-2 ring-red-500' : ''}`}
                    onClick={() => selectDriver(driver)} data-testid={`driver-card-${driver.driver_id}`}>
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-3 min-w-0">
                        <div className={`h-10 w-10 rounded-full flex items-center justify-center flex-shrink-0 ${driver.status === 'active' ? 'bg-red-100' : driver.status === 'pending' ? 'bg-yellow-100' : 'bg-gray-100'}`}>
                          <span className="text-sm font-bold text-red-600">{driver.name?.[0] || '?'}</span>
                        </div>
                        <div className="min-w-0">
                          <p className="font-semibold text-sm text-black truncate">{driver.name}</p>
                          <p className="text-xs text-gray-500 truncate">{driver.vehicle_assigned || 'No vehicle'}</p>
                        </div>
                      </div>
                      <div className="flex items-center gap-2 flex-shrink-0">
                        {driver.performance?.avg_rating > 0 && (
                          <span className="text-xs text-yellow-600 flex items-center gap-0.5"><Star className="h-3 w-3" />{driver.performance.avg_rating}</span>
                        )}
                        <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-medium border ${AVAIL_COLORS[driver.availability_status] || 'bg-gray-100'}`}>
                          <AvailIcon className="h-2.5 w-2.5" />
                          <span className="hidden sm:inline">{driver.availability_status?.replace('_', ' ')}</span>
                        </span>
                      </div>
                    </div>
                    {/* Mobile: quick action row */}
                    <div className="flex items-center gap-1.5 mt-2 lg:hidden">
                      {driver.phone && <a href={`tel:${driver.phone}`} className="text-[10px] font-semibold text-blue-600 bg-blue-50 px-2.5 py-1.5 rounded-lg active:bg-blue-100" onClick={e => e.stopPropagation()}><Phone className="h-3 w-3 inline mr-0.5" /> Call</a>}
                      {['available','on_break','unavailable'].map(s => (
                        <button key={s} onClick={(e) => { e.stopPropagation(); handleAvailability(driver.driver_id, s); }}
                          className={`text-[10px] font-semibold px-2 py-1.5 rounded-lg ${driver.availability_status === s ? (s === 'available' ? 'bg-green-600 text-white' : s === 'on_break' ? 'bg-yellow-600 text-white' : 'bg-red-600 text-white') : 'bg-gray-100 text-gray-600 active:bg-gray-200'}`}>
                          {s === 'on_break' ? 'Break' : s === 'available' ? 'On' : 'Off'}
                        </button>
                      ))}
                    </div>
                  </Card>
                );
              })}
              {filtered.length === 0 && <p className="text-center text-gray-500 py-8">No drivers found</p>}
            </div>
          </div>

          {/* Driver Detail Panel — Full-screen drawer on mobile */}
          {selectedDriver && (
            <div className="fixed inset-0 bg-white z-50 overflow-y-auto lg:relative lg:inset-auto lg:z-auto lg:bg-transparent lg:w-2/3" data-testid="driver-detail">
              {/* Mobile back header */}
              <div className="sticky top-0 bg-black text-white px-4 py-3 flex items-center justify-between z-10 lg:hidden">
                <div className="flex items-center gap-3">
                  <button onClick={() => setSelectedDriver(null)}><ArrowLeft className="h-5 w-5 text-gray-400" /></button>
                  <div>
                    <h2 className="font-bold text-sm">{selectedDriver.name}</h2>
                    <p className="text-[10px] text-gray-400">{selectedDriver.driver_id}</p>
                  </div>
                </div>
                <div className="flex items-center gap-1">
                  {['available','on_break','unavailable'].map(s => (
                    <button key={s}
                      className={`text-[10px] font-bold px-2.5 py-1.5 rounded-full ${selectedDriver.availability_status === s ? (s === 'available' ? 'bg-green-600 text-white' : s === 'on_break' ? 'bg-yellow-600 text-white' : 'bg-red-600 text-white') : 'bg-gray-800 text-gray-400'}`}
                      onClick={() => handleAvailability(selectedDriver.driver_id, s)}
                      data-testid={`mob-avail-${s}`}>
                      {s === 'on_break' ? 'Break' : s === 'available' ? 'On' : 'Off'}
                    </button>
                  ))}
                </div>
              </div>
              <Card className="border-gray-200 lg:border">
                {/* Desktop header */}
                <div className="p-4 border-b border-gray-200 items-center justify-between hidden lg:flex">
                  <div>
                    <h2 className="text-lg font-bold text-black">{selectedDriver.name}</h2>
                    <p className="text-sm text-gray-500">{selectedDriver.driver_id}</p>
                  </div>
                  <div className="flex items-center gap-2">
                    {['available', 'on_break', 'unavailable'].map(s => (
                      <Button key={s} size="sm" variant={selectedDriver.availability_status === s ? 'default' : 'outline'}
                        className={`text-xs ${selectedDriver.availability_status === s ? (s === 'available' ? 'bg-green-600' : s === 'on_break' ? 'bg-yellow-600' : 'bg-red-600') : 'border-gray-300'}`}
                        onClick={() => handleAvailability(selectedDriver.driver_id, s)}
                        data-testid={`avail-${s}`}>
                        {s.replace('_', ' ')}
                      </Button>
                    ))}
                  </div>
                </div>

                <Tabs value={detailTab} onValueChange={setDetailTab}>
                  <div className="overflow-x-auto border-b">
                    <TabsList className="bg-transparent w-max">
                      {[
                        { id: 'info', icon: Users, label: 'Info' },
                        { id: 'schedule', icon: Calendar, label: 'Schedule' },
                        { id: 'performance', icon: TrendingUp, label: 'Performance' },
                        { id: 'payroll', icon: DollarSign, label: 'Payroll' },
                        { id: 'messages', icon: MessageSquare, label: 'Messages' },
                        { id: 'documents', icon: FileText, label: 'Docs' },
                      ].map(({ id, icon: Icon, label }) => (
                        <TabsTrigger key={id} value={id} className="text-xs data-[state=active]:bg-red-600 data-[state=active]:text-white" data-testid={`detail-tab-${id}`}>
                          <Icon className="h-3 w-3 mr-1" />{label}
                        </TabsTrigger>
                      ))}
                    </TabsList>
                  </div>

                  {/* Info Tab */}
                  <TabsContent value="info" className="p-4 space-y-4">
                    <div className="grid sm:grid-cols-2 gap-4 text-sm">
                      <div className="flex items-center gap-2"><Mail className="h-4 w-4 text-gray-400" /><span>{selectedDriver.email || 'N/A'}</span></div>
                      <div className="flex items-center gap-2"><Phone className="h-4 w-4 text-gray-400" /><span>{selectedDriver.phone || 'N/A'}</span></div>
                      <div className="flex items-center gap-2"><Shield className="h-4 w-4 text-gray-400" /><span>License: {selectedDriver.license_number || 'N/A'}</span></div>
                      <div className="flex items-center gap-2"><Car className="h-4 w-4 text-gray-400" /><span>{selectedDriver.vehicle_assigned || 'Not assigned'}</span></div>
                      <div className="flex items-center gap-2"><DollarSign className="h-4 w-4 text-gray-400" /><span>${selectedDriver.hourly_rate}/hr</span></div>
                      <div className="flex items-center gap-2"><Calendar className="h-4 w-4 text-gray-400" /><span>Hired: {selectedDriver.hire_date || 'N/A'}</span></div>
                    </div>
                    {selectedDriver.notes && <div className="bg-gray-50 rounded p-3 text-sm text-gray-600">{selectedDriver.notes}</div>}
                    <div className="flex gap-2 pt-2">
                      {selectedDriver.status === 'pending' && (
                        <Button size="sm" onClick={() => handleUpdateStatus(selectedDriver.driver_id, 'active')} className="bg-green-600 hover:bg-green-700" data-testid="approve-driver">Approve</Button>
                      )}
                      {selectedDriver.status === 'active' && (
                        <Button size="sm" variant="outline" onClick={() => handleUpdateStatus(selectedDriver.driver_id, 'inactive')} className="border-gray-300" data-testid="deactivate-driver">Deactivate</Button>
                      )}
                      {selectedDriver.status === 'inactive' && (
                        <Button size="sm" onClick={() => handleUpdateStatus(selectedDriver.driver_id, 'active')} className="bg-green-600 hover:bg-green-700" data-testid="reactivate-driver">Reactivate</Button>
                      )}
                      <Button size="sm" variant="outline" onClick={() => handleDeleteDriver(selectedDriver.driver_id)} className="border-red-300 text-red-600 hover:bg-red-600 hover:text-white" data-testid="delete-driver">
                        <Trash2 className="h-3 w-3 mr-1" />Delete
                      </Button>
                    </div>
                  </TabsContent>

                  {/* Schedule Tab */}
                  <TabsContent value="schedule" className="p-4">
                    <h4 className="text-sm font-bold text-gray-500 uppercase mb-4">Weekly Schedule</h4>
                    <div className="space-y-2">
                      {DAYS.map(day => {
                        const slots = selectedDriver.weekly_schedule?.[day] || [];
                        return (
                          <div key={day} className="flex items-center gap-2 sm:gap-3 py-2 border-b border-gray-100">
                            <span className="w-10 text-sm font-bold text-gray-700">{DAY_LABELS[day]}</span>
                            {slots.length > 0 ? (
                              slots.map((slot, i) => (
                                <div key={i} className="flex items-center gap-1">
                                  <Input type="time" defaultValue={slot.start} className="h-11 sm:h-8 w-28 text-sm"
                                    onBlur={e => {
                                      const updated = [...slots];
                                      updated[i] = { ...updated[i], start: e.target.value };
                                      handleScheduleUpdate(selectedDriver.driver_id, day, updated);
                                    }} />
                                  <span className="text-gray-400">-</span>
                                  <Input type="time" defaultValue={slot.end} className="h-11 sm:h-8 w-28 text-sm"
                                    onBlur={e => {
                                      const updated = [...slots];
                                      updated[i] = { ...updated[i], end: e.target.value };
                                      handleScheduleUpdate(selectedDriver.driver_id, day, updated);
                                    }} />
                                </div>
                              ))
                            ) : (
                              <span className="text-xs text-gray-400">Off</span>
                            )}
                            <Button size="sm" variant="ghost" className="text-xs ml-auto h-9 px-3"
                              onClick={() => handleScheduleUpdate(selectedDriver.driver_id, day, slots.length > 0 ? [] : [{ start: '09:00', end: '18:00' }])}>
                              {slots.length > 0 ? 'Set Off' : 'Set On'}
                            </Button>
                          </div>
                        );
                      })}
                    </div>
                  </TabsContent>

                  {/* Performance Tab */}
                  <TabsContent value="performance" className="p-4">
                    {performance && (
                      <div className="space-y-4">
                        <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
                          {[
                            { label: 'Total Rides', value: performance.performance?.total_rides || 0 },
                            { label: 'Total Hours', value: `${performance.performance?.total_hours || 0}h` },
                            { label: 'Avg Rating', value: performance.performance?.avg_rating || '-' },
                            { label: 'On-Time %', value: `${performance.performance?.on_time_percent || 100}%` },
                            { label: 'Revenue', value: `$${performance.performance?.total_revenue || 0}` },
                            { label: 'Ratings Count', value: performance.performance?.total_ratings || 0 },
                          ].map((s, i) => (
                            <div key={i} className="bg-gray-50 rounded-lg p-3 text-center">
                              <p className="text-xl font-bold text-black">{s.value}</p>
                              <p className="text-[10px] text-gray-500">{s.label}</p>
                            </div>
                          ))}
                        </div>
                        {performance.recent_ratings?.length > 0 && (
                          <div>
                            <h4 className="text-sm font-bold text-gray-500 mb-2">Recent Ratings</h4>
                            <div className="space-y-2">
                              {performance.recent_ratings.map((r, i) => (
                                <div key={i} className="flex items-center justify-between py-2 border-b border-gray-100">
                                  <div className="flex items-center gap-2">
                                    {[1,2,3,4,5].map(s => <Star key={s} className={`h-3 w-3 ${s <= r.rating ? 'text-yellow-500 fill-yellow-500' : 'text-gray-300'}`} />)}
                                    {r.comment && <span className="text-xs text-gray-600 ml-2">"{r.comment}"</span>}
                                  </div>
                                  <span className="text-xs text-gray-400">{new Date(r.created_at).toLocaleDateString()}</span>
                                </div>
                              ))}
                            </div>
                          </div>
                        )}
                      </div>
                    )}
                  </TabsContent>

                  {/* Payroll Tab */}
                  <TabsContent value="payroll" className="p-4">
                    <div className="overflow-x-auto">
                      <table className="w-full text-sm">
                        <thead className="bg-gray-50"><tr>
                          <th className="text-left py-2 px-3 text-xs font-semibold text-gray-600">Period</th>
                          <th className="text-right py-2 px-3 text-xs font-semibold text-gray-600">Rides</th>
                          <th className="text-right py-2 px-3 text-xs font-semibold text-gray-600">Hours</th>
                          <th className="text-right py-2 px-3 text-xs font-semibold text-gray-600">Base</th>
                          <th className="text-right py-2 px-3 text-xs font-semibold text-gray-600">Bonus</th>
                          <th className="text-right py-2 px-3 text-xs font-semibold text-gray-600">Net</th>
                          <th className="text-center py-2 px-3 text-xs font-semibold text-gray-600">Status</th>
                        </tr></thead>
                        <tbody className="divide-y divide-gray-100">
                          {payroll.map((p, i) => (
                            <tr key={i} className="hover:bg-gray-50">
                              <td className="py-2 px-3 font-medium">{p.period}</td>
                              <td className="py-2 px-3 text-right">{p.total_rides}</td>
                              <td className="py-2 px-3 text-right">{p.total_hours}h</td>
                              <td className="py-2 px-3 text-right">${p.base_earnings}</td>
                              <td className="py-2 px-3 text-right text-green-600">+${p.bonuses}</td>
                              <td className="py-2 px-3 text-right font-bold">${p.net_earnings}</td>
                              <td className="py-2 px-3 text-center">
                                <span className={`text-xs px-2 py-0.5 rounded-full ${p.status === 'paid' ? 'bg-green-100 text-green-700' : p.status === 'current' ? 'bg-blue-100 text-blue-700' : 'bg-yellow-100 text-yellow-700'}`}>
                                  {p.status}
                                </span>
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                      {payroll.length === 0 && <p className="text-center text-gray-500 py-8">No payroll records</p>}
                    </div>
                  </TabsContent>

                  {/* Messages Tab */}
                  <TabsContent value="messages" className="p-0">
                    <div className="h-72 overflow-y-auto p-4 space-y-3 bg-gray-50" data-testid="messages-area">
                      {messages.length === 0 && <p className="text-center text-gray-400 text-sm py-8">No messages yet</p>}
                      {messages.map((msg, i) => (
                        <div key={i} className={`flex ${msg.from_id === 'admin' ? 'justify-end' : 'justify-start'}`}>
                          <div className={`max-w-[70%] rounded-lg px-3 py-2 text-sm ${msg.from_id === 'admin' ? 'bg-red-600 text-white' : 'bg-white border border-gray-200 text-black'}`}>
                            <p>{msg.text}</p>
                            <p className={`text-[10px] mt-1 ${msg.from_id === 'admin' ? 'text-red-200' : 'text-gray-400'}`}>
                              {new Date(msg.created_at).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                            </p>
                          </div>
                        </div>
                      ))}
                    </div>
                    <div className="p-3 border-t flex gap-2">
                      <Input value={msgText} onChange={e => setMsgText(e.target.value)} placeholder="Type a message..."
                        onKeyDown={e => e.key === 'Enter' && handleSendMessage()} className="flex-1 h-11 sm:h-9" data-testid="message-input" />
                      <Button onClick={handleSendMessage} className="bg-red-600 hover:bg-red-700 h-11 sm:h-9" data-testid="send-message-btn">
                        <Send className="h-4 w-4" />
                      </Button>
                    </div>
                  </TabsContent>

                  {/* Documents Tab */}
                  <TabsContent value="documents" className="p-4">
                    {/* Upload Form */}
                    <div className="mb-4 p-3 bg-gray-50 rounded-lg border border-gray-200">
                      <h4 className="text-xs font-bold text-gray-500 uppercase mb-2">Upload Document</h4>
                      <div className="space-y-2 sm:space-y-0 sm:flex sm:flex-row sm:gap-2 sm:flex-wrap">
                        <select value={docUpload.type} onChange={e => setDocUpload(p => ({...p, type: e.target.value}))}
                          className="h-11 sm:h-8 text-sm sm:text-xs border border-gray-200 rounded px-2 bg-white w-full sm:w-auto" data-testid="doc-type-select">
                          <option value="drivers_license">Driver's License</option>
                          <option value="insurance">Insurance</option>
                          <option value="registration">Registration</option>
                          <option value="background_check">Background Check</option>
                          <option value="medical">Medical Cert</option>
                          <option value="chauffeur_permit">Chauffeur Permit</option>
                          <option value="other">Other</option>
                        </select>
                        <Input type="date" value={docUpload.expires_at} onChange={e => setDocUpload(p => ({...p, expires_at: e.target.value}))}
                          className="h-11 sm:h-8 text-sm sm:text-xs w-full sm:w-36" placeholder="Expiry date" />
                        <input type="file" accept=".pdf,.jpg,.jpeg,.png,.webp,.doc,.docx" onChange={e => setDocUpload(p => ({...p, file: e.target.files[0]}))}
                          className="text-sm sm:text-xs w-full sm:w-auto" data-testid="doc-file-input" />
                        <Button size="sm" disabled={!docUpload.file || docUploading} onClick={handleDocUpload}
                          className="bg-red-600 hover:bg-red-700 text-xs h-11 sm:h-8 w-full sm:w-auto" data-testid="upload-doc-btn">
                          {docUploading ? <Loader2 className="h-3 w-3 animate-spin mr-1" /> : <Upload className="h-3 w-3 mr-1" />} Upload
                        </Button>
                      </div>
                    </div>
                    <div className="space-y-2">
                      {documents.map(doc => (
                        <div key={doc.document_id} className="flex items-center justify-between py-2 border-b border-gray-100">
                          <div className="flex items-center gap-3">
                            <FileText className="h-5 w-5 text-gray-400" />
                            <div>
                              <p className="text-sm font-medium text-black">
                                {doc.file_url ? <a href={doc.file_url} target="_blank" rel="noopener noreferrer" className="hover:text-red-600 underline">{doc.name}</a> : doc.name}
                              </p>
                              <p className="text-xs text-gray-500">{doc.type?.replace(/_/g,' ')} {doc.expires_at && `| Expires: ${doc.expires_at}`} {doc.file_size && `| ${(doc.file_size/1024/1024).toFixed(1)}MB`}</p>
                            </div>
                          </div>
                          <span className={`text-xs px-2 py-0.5 rounded-full ${doc.status === 'valid' ? 'bg-green-100 text-green-700' : 'bg-red-100 text-red-700'}`}>
                            {doc.status}
                          </span>
                        </div>
                      ))}
                      {documents.length === 0 && <p className="text-center text-gray-500 py-8">No documents uploaded</p>}
                    </div>
                  </TabsContent>
                </Tabs>
              </Card>
            </div>
          )}
        </div>
      </div>

      {/* Add Driver Modal */}
      {showForm && (
        <div className="fixed inset-0 bg-black/50 flex items-end sm:items-center justify-center z-50" data-testid="add-driver-modal">
          <Card className="w-full sm:max-w-lg bg-white rounded-t-2xl sm:rounded-2xl max-h-[90vh] overflow-y-auto">
            <div className="p-4 border-b flex items-center justify-between sticky top-0 bg-white z-10 rounded-t-2xl">
              <h3 className="text-lg font-bold">Add New Driver</h3>
              <Button variant="ghost" size="sm" onClick={() => setShowForm(false)}><X className="h-5 w-5" /></Button>
            </div>
            <form onSubmit={handleCreate} className="p-4 sm:p-6 space-y-3 sm:space-y-4">
              <div><Label>Name *</Label><Input value={newDriver.name} onChange={e => setNewDriver({...newDriver, name: e.target.value})} required className="h-11 sm:h-9" data-testid="new-driver-name" /></div>
              <div className="grid grid-cols-2 gap-3 sm:gap-4">
                <div><Label>Email</Label><Input type="email" value={newDriver.email} onChange={e => setNewDriver({...newDriver, email: e.target.value})} className="h-11 sm:h-9" data-testid="new-driver-email" /></div>
                <div><Label>Phone</Label><Input value={newDriver.phone} onChange={e => setNewDriver({...newDriver, phone: e.target.value})} className="h-11 sm:h-9" data-testid="new-driver-phone" /></div>
              </div>
              <div className="grid grid-cols-2 gap-3 sm:gap-4">
                <div><Label>License #</Label><Input value={newDriver.license_number} onChange={e => setNewDriver({...newDriver, license_number: e.target.value})} className="h-11 sm:h-9" data-testid="new-driver-license" /></div>
                <div><Label>Hourly Rate ($)</Label><Input type="number" value={newDriver.hourly_rate} onChange={e => setNewDriver({...newDriver, hourly_rate: parseFloat(e.target.value) || 0})} className="h-11 sm:h-9" data-testid="new-driver-rate" /></div>
              </div>
              <div><Label>Vehicle Assigned</Label><Input value={newDriver.vehicle_assigned} onChange={e => setNewDriver({...newDriver, vehicle_assigned: e.target.value})} placeholder="e.g. Mercedes S-Class" className="h-11 sm:h-9" data-testid="new-driver-vehicle" /></div>
              <div><Label>Notes</Label><Input value={newDriver.notes} onChange={e => setNewDriver({...newDriver, notes: e.target.value})} placeholder="Optional notes" className="h-11 sm:h-9" /></div>
              <Button type="submit" className="w-full bg-red-600 hover:bg-red-700 h-12 sm:h-9" data-testid="submit-driver">Add Driver</Button>
            </form>
          </Card>
        </div>
      )}
    </div>
  );
};

export default AdminDrivers;
